import { DetailedHTMLProps, InputHTMLAttributes, ReactElement, ReactNode, useContext } from 'react'
import classNames from 'classnames'
import { FormErrorMessage } from '@/components/pages/checkout/booking-page/booking-form/FormErrorMessage'
import {
  BookingPageContext,
  getError,
} from '@/components/pages/checkout/booking-page/BookingPageContext'
import { FieldPath } from 'react-hook-form/dist/types/path'
import { BookingFormData } from '@/types/Booking'
import { twMerge } from 'tailwind-merge'
import { isInTestGroup } from '@/lib/ab-tests/test-variants'
import { TestVariant } from '@/lib/ab-tests/test-definitions'

interface BookingFormInputElementProps {
  labelValue: string
  name: FieldPath<BookingFormData>
  formatter?: (value: string) => string
  locked?: boolean
  ignoreFormHandler?: boolean
}

export function getInputBorderStyles(error: boolean) {
  return classNames({
    'border-red-500 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500': error,
    'border-stone-300': !error,
  })
}

export function BookingFormInputLabel(props: { htmlFor: string; children: ReactNode }) {
  const abMandatoryStars = isInTestGroup(TestVariant.MandatoryStars)

  return (
    <label htmlFor={props.htmlFor} className="mb-1 block">
      {props.children} {abMandatoryStars && '*'}
    </label>
  )
}

export default function BookingFormInputElement({
  labelValue,
  formatter,
  locked,
  ignoreFormHandler,
  ...props
}: DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> &
  BookingFormInputElementProps): ReactElement {
  const formHandler = useContext(BookingPageContext).formHandler
  const error = getError(props.name, formHandler)
  const lockedOnChange = locked ? { onChange: () => false } : {}

  const register = ignoreFormHandler ? {} : formHandler.register(props.name)

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    if (props.onBlur) {
      props.onBlur(e)
    }

    if (!ignoreFormHandler) {
      void formHandler.trigger(props.name)
    }
  }

  return (
    <div className={twMerge('min-w-0', props.className)}>
      <BookingFormInputLabel htmlFor={props.name}>{labelValue}</BookingFormInputLabel>
      <input
        {...props}
        className={classNames('w-full rounded-md', getInputBorderStyles(!!error))}
        id={props.name}
        {...register}
        onBlur={handleBlur}
        value={
          formatter
            ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              formatter(formHandler.getValues()[props.name] || '')
            : props.value
        }
        {...lockedOnChange}
      />
      <FormErrorMessage message={error?.message} />
    </div>
  )
}
